import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import logo from "@images/logo.svg";

function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles: { x: number; y: number; vx: number; vy: number; r: number; alpha: number }[] = [];
    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 1.5 + 0.5,
        alpha: Math.random() * 0.4 + 0.1,
      });
    }

    let animId: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(245, 158, 11, ${p.alpha})`;
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(245, 158, 11, ${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}

interface NavbarProps {
  scrolled: boolean;
}

function Navbar({ scrolled }: NavbarProps) {
  const { t } = useTranslation();
  const { currentLang, toggleLanguage } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const navItems = [
    { label: t("nav.about"), id: "about" },
    { label: t("nav.services"), id: "services" },
    { label: t("nav.momo"), id: "momo" },
    { label: t("nav.team"), id: "team" },
    { label: t("nav.contact"), id: "contact" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? "shadow-lg" : "bg-transparent"}`}
      style={scrolled ? { backgroundColor: "oklch(0.10 0.001 10)" } : {}}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="cursor-pointer">
          <img src={logo} alt="IKOB 로고" className="h-8" />
        </button>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-7">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="cursor-pointer text-sm font-label whitespace-nowrap transition-colors duration-200"
              style={{ color: "rgba(255,255,255,0.55)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "oklch(0.74 0.18 55)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.55)")}
            >
              {item.label}
            </button>
          ))}

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="cursor-pointer whitespace-nowrap px-3 py-1.5 rounded-full border text-xs font-label font-semibold transition-all duration-200"
            style={{
              borderColor: "rgba(245,158,11,0.40)",
              color: "oklch(0.74 0.18 55)",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(245,158,11,0.12)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent";
            }}
          >
            {currentLang === "ko" ? "EN" : "한국어"}
          </button>

          {/* CTA */}
          <button
            onClick={() => scrollTo("contact")}
            className="cursor-pointer whitespace-nowrap font-label font-semibold px-5 py-2 rounded-full transition-colors duration-200 text-sm"
            style={{ backgroundColor: "oklch(0.74 0.18 55)", color: "oklch(0.10 0.001 10)" }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = "oklch(0.80 0.18 58)")}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = "oklch(0.74 0.18 55)")}
          >
            {t("nav.cta")}
          </button>
        </div>

        {/* Mobile hamburger */}
        <div className="md:hidden flex items-center gap-3">
          <button
            onClick={toggleLanguage}
            className="cursor-pointer px-2.5 py-1 rounded-full border text-xs font-label font-bold whitespace-nowrap"
            style={{ borderColor: "rgba(245,158,11,0.40)", color: "oklch(0.74 0.18 55)" }}
          >
            {currentLang === "ko" ? "EN" : "한"}
          </button>
          <button
            className="cursor-pointer w-8 h-8 flex items-center justify-center"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="메뉴 열기"
          >
            <i className={`ri-${menuOpen ? "close" : "menu"}-line text-2xl`} style={{ color: "white" }}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          className="md:hidden border-t px-6 py-4 flex flex-col gap-4"
          style={{
            backgroundColor: "oklch(0.10 0.001 10)",
            borderColor: "rgba(255,255,255,0.06)",
          }}
        >
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="cursor-pointer text-sm font-label text-left whitespace-nowrap transition-colors"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={() => scrollTo("contact")}
            className="cursor-pointer w-fit whitespace-nowrap font-label font-semibold px-5 py-2 rounded-full text-sm mt-1"
            style={{ backgroundColor: "oklch(0.74 0.18 55)", color: "oklch(0.10 0.001 10)" }}
          >
            {t("nav.cta")}
          </button>
        </div>
      )}
    </nav>
  );
}

export default function HeroSection() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Navbar scrolled={scrolled} />
      <section
        id="hero"
        className="relative w-full h-screen min-h-[700px] flex flex-col items-center justify-center overflow-hidden"
        style={{ backgroundColor: "oklch(0.10 0.001 10)" }}
      >
        <ParticleCanvas />

        {/* Glow */}
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, rgba(245,158,11,0.10) 0%, transparent 70%)",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -60%)",
          }}
        />

        <div className="relative z-10 flex flex-col items-center text-center px-4 max-w-4xl w-full">
          {/* Badge */}
          <div className="animate-fade-in opacity-0-init mb-6">
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-label tracking-widest uppercase"
              style={{ borderColor: "rgba(245,158,11,0.35)", color: "oklch(0.74 0.18 55)" }}
            >
              <i className="ri-sparkling-line"></i>
              {t("hero.badge")}
            </span>
          </div>

          {/* Title */}
          <h1
            className="animate-fade-in-up opacity-0-init delay-100 font-heading font-black leading-tight mb-6"
            style={{ fontSize: "clamp(2.4rem, 7vw, 5rem)", color: "white" }}
          >
            {t("hero.line1")}<br />
            <span style={{ color: "oklch(0.74 0.18 55)" }}>{t("hero.highlight")}</span><br />
            {t("hero.line2")}
          </h1>

          {/* Sub */}
          <p
            className="animate-fade-in-up opacity-0-init delay-200 font-label text-sm md:text-base leading-relaxed mb-3 text-center"
            style={{ color: "rgba(255,255,255,0.55)", maxWidth: "480px" }}
          >
            {t("hero.sub1")}<br />
            {t("hero.sub2")}
          </p>

          {/* Tagline */}
          <p
            className="animate-fade-in-up opacity-0-init delay-300 font-label text-sm tracking-widest mb-10"
            style={{ color: "rgba(255,255,255,0.35)" }}
          >
            <span style={{ color: "rgba(255,255,255,0.6)" }}>I</span>nspire&nbsp;
            <span style={{ color: "oklch(0.74 0.18 55)" }}>·</span>
            &nbsp;<span style={{ color: "rgba(255,255,255,0.6)" }}>K</span>now&nbsp;
            <span style={{ color: "oklch(0.74 0.18 55)" }}>·</span>
            &nbsp;<span style={{ color: "oklch(0.74 0.18 55)" }}>O</span>ptimize&nbsp;
            <span style={{ color: "oklch(0.74 0.18 55)" }}>·</span>
            &nbsp;<span style={{ color: "rgba(255,255,255,0.6)" }}>B</span>uild
          </p>

          {/* CTA */}
          <button
            onClick={() => {
              const el = document.getElementById("about");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="animate-fade-in-up opacity-0-init delay-400 cursor-pointer whitespace-nowrap font-label font-semibold px-8 py-3.5 rounded-full transition-all duration-200 flex items-center gap-2"
            style={{ backgroundColor: "oklch(0.74 0.18 55)", color: "oklch(0.10 0.001 10)" }}
          >
            <i className="ri-arrow-down-line"></i>
            {t("hero.cta")}
          </button>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in opacity-0-init delay-600">
          <div className="w-px h-10 bg-gradient-to-b from-transparent to-primary-500/50"></div>
          <span className="text-xs font-label tracking-widest" style={{ color: "rgba(255,255,255,0.3)" }}>
            Scroll
          </span>
        </div>
      </section>
    </>
  );
}